/**
 * 商品稿樣挑選(REQ-20260806003 單選 → REQ-20260809004 改複選)。
 *
 * 原本寫死在 `CreateReceivableModal` 裡,理由是「只有訪談建單會挑稿樣」。
 * REQ-20260811004 讓出貨管理的建單 / 編輯表單也要挑,第二個呼叫點出現才抽出來 ——
 * 「哪些附件算候選圖片」與「勾選順序 = 列印排列順序」是兩張表單必須一致的規則,
 * 複製一份會慢慢分岔(判斷同 `MultiSelectField`)。
 *
 * 候選圖片的篩選放在元件裡:呼叫端把該筆訪談的附件全部給進來就好。
 * 沒有圖片附件時整塊不顯示 —— 空的稿樣框只會讓人以為圖片載入失敗。
 */
import type { InterviewAttachment } from '@app/shared';
import { useAttachmentUrl } from '../features/attachments/api';

/**
 * 哪些附件算候選稿樣圖片。REQ-20260903005 起兩個呼叫端要自己判斷「有圖可挑卻一張
 * 都沒勾」,所以規則從元件內部抬成匯出的函式 —— 呼叫端問的必須是同一個問題,
 * 複製一份 `startsWith('image/')` 出去就會慢慢分岔(理由同上面那段註解)。
 */
export function artworkImages(attachments: InterviewAttachment[]): InterviewAttachment[] {
  return attachments.filter((a) => a.mimeType.startsWith('image/'));
}

export function ArtworkPicker(props: {
  /** 該筆訪談的附件(不必先過濾,非圖片的會被擋掉) */
  attachments: InterviewAttachment[];
  /** 已選的附件 id,順序 = 列印排列順序 */
  selected: number[];
  onChange: (next: number[]) => void;
  /** data-testid / checkbox name 的前綴:`{testId}-picker`、`{testId}-option` */
  testId: string;
}): JSX.Element | null {
  const { attachments, selected, onChange, testId } = props;
  const images = artworkImages(attachments);
  if (images.length === 0) return null;

  const toggle = (id: number): void => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <fieldset className="grid gap-1" data-testid={`${testId}-picker`}>
      <legend className="text-sm text-slate-700">
        商品稿樣(列印訂單時帶入稿樣區,可複選)
      </legend>
      {/* 沒有「不指定」選項:一張都不勾就是不指定(REQ-20260809004) */}
      <div className="flex flex-wrap gap-2">
        {images.map((a) => (
          <ArtworkOption
            key={a.uuid}
            attachment={a}
            name={testId}
            testId={`${testId}-option`}
            selected={selected.includes(a.id)}
            onToggle={() => toggle(a.id)}
          />
        ))}
      </div>
    </fieldset>
  );
}

/**
 * 稿樣候選縮圖。signed URL 要逐張取,所以每張圖包成一個元件(hook 不能寫在迴圈裡)。
 */
function ArtworkOption(props: {
  attachment: InterviewAttachment;
  name: string;
  testId: string;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { attachment, name, testId, selected, onToggle } = props;
  const { data: url } = useAttachmentUrl(attachment.uuid);
  return (
    <label
      className="flex flex-col items-center gap-1 text-xs text-slate-600 cursor-pointer"
      title={attachment.originalName}
    >
      <span
        className={`w-20 h-20 border rounded overflow-hidden flex items-center justify-center bg-slate-50 ${
          selected ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        {url ? (
          <img src={url} alt={attachment.originalName} className="w-full h-full object-contain" />
        ) : (
          <span className="text-slate-400">載入中…</span>
        )}
      </span>
      <input
        type="checkbox"
        name={name}
        checked={selected}
        onChange={onToggle}
        data-testid={testId}
      />
    </label>
  );
}
